import {useMemo} from 'react';

import {getSafely} from 'shared/web';

import type {ActionDefinitionObject} from '../types/ActionDefinition';
import type {FeGridSettings} from '../types/Api';
import type {RowData} from '../types/RowData';
import {classifyRowAction} from '../utils/classifyRowAction';
import {getActionButtonSize} from '../utils/getActionButtonSize';
import {getActionColumnWidth} from '../utils/getActionColumnWidth';

type UseGridActionColumnParams = {
  rows: RowData[];
  previousRows: RowData[];
  actionsDefinition: ActionDefinitionObject | null;
  gridSettings: FeGridSettings;
  actionColumnEnabled: boolean;
  isMobile: boolean;
};

type UseGridActionColumnReturn = {
  actionColumnWidth: number;
};

/**
 * Computes the action column width based on visible row actions.
 *
 * Iterates all rows to find the max icon count and whether overflow actions exist,
 * then calculates the required column width from button size and slot count.
 *
 * @mandatory Iterates all rows on every render. Memoizing prevents unnecessary
 * recomputation when unrelated state (loading, selection, column reorder) changes.
 * Used for action column width sizing — must be stable to avoid layout thrashing.
 */
export const useGridActionColumn = (
  params: UseGridActionColumnParams
): UseGridActionColumnReturn => {
  /**
   * Compute maxIconCount (IconButtons per row) and whether any overflow actions exist.
   * - iconActions: singleAction === true AND has icon
   * - overflowActions: singleAction === true AND no icon AND type !== 'ROW_CLICK'
   */
  const {maxIconCount, hasOverflow} = useMemo(() => {
    const displayRows = params.rows.length > 0 ? params.rows : params.previousRows;
    let maxIcons = 0;
    let overflow = false;

    for (const row of displayRows) {
      const rowActions = row.actions;
      let iconCount = 0;
      const allKeys = [...rowActions.primary, ...rowActions.secondary];

      for (const key of allKeys) {
        const def = getSafely(params.actionsDefinition ?? {}, key);
        if (!def || !def.singleAction) {
          continue;
        }
        const slot = classifyRowAction(def);
        if (slot === 'icon') {
          iconCount++;
        } else if (slot === 'overflow') {
          overflow = true;
        }
      }
      if (iconCount > maxIcons) {
        maxIcons = iconCount;
      }
    }

    return {maxIconCount: maxIcons, hasOverflow: overflow};
  }, [params.rows, params.previousRows, params.actionsDefinition]);

  const buttonSizePx =
    getActionButtonSize(params.gridSettings.rowHeight, params.isMobile) === 'small' ? 24 : 32;

  const totalButtonSlots = maxIconCount + (hasOverflow ? 1 : 0);
  const actionColumnWidth = params.actionColumnEnabled
    ? getActionColumnWidth(totalButtonSlots, buttonSizePx)
    : 0;

  return {actionColumnWidth};
};
